/**
 * Visit Token — contrato de dominio compartido entre PWA y API.
 *
 * Autoridad: docs/09-VISIT-TOKEN-AND-INTEGRITY.md (parámetros y umbrales)
 *            docs/adr/002-backend-stack.md (forma técnica)
 *            docs/adr/006-online-only.md (emisión síncrona: GU requiere conexión)
 *            docs/adr/016-forma-del-claim-de-visita.md (la forma del claim)
 *
 * PRINCIPIO RECTOR: el cliente propone, el servidor dispone.
 * Todo lo que se calcula aquí en el cliente sirve ÚNICAMENTE para la UI
 * (mostrar "estás aquí ✓" antes de dejar escribir). El servidor recalcula
 * el Visit Trust Score desde cero con las muestras crudas e ignora por
 * completo cualquier veredicto que venga del dispositivo.
 */

import { z } from 'zod';

/** Ventana de validez del token. Doc 09 §5: 72 h, no 48. */
export const TOKEN_VALIDITY_HOURS = 72;

/**
 * Nota sobre la ventana de gracia (ADR-006): el token se emite EN EL LUGAR
 * y se consume después, desde donde sea, dentro de las 72 h. Lo que se
 * eliminó es capturar evidencia sin conexión, no la ventana de gracia:
 * "comí el viernes, opino el domingo" sigue funcionando igual.
 */

/** Cooldown para volver a reclamar token en el mismo lugar. Doc 09 §5. */
export const SAME_PLACE_COOLDOWN_HOURS = 6;

/**
 * Perfiles de geofence. Doc 09 §5.
 * Un puesto de calle y un centro comercial no pueden compartir parámetros:
 * el GPS se comporta de forma radicalmente distinta.
 */
export const GEOFENCE_PROFILES = {
  standard: { radiusM: 120, minDwellSec: 240, maxAccuracyM: 100 },
  indoor: { radiusM: 180, minDwellSec: 240, maxAccuracyM: 200 },
  street: { radiusM: 150, minDwellSec: 180, maxAccuracyM: 120 },
  large_venue: { radiusM: 250, minDwellSec: 240, maxAccuracyM: 200 },
} as const;

export type GeofenceProfile = keyof typeof GEOFENCE_PROFILES;

/** Umbrales del Visit Trust Score. Doc 09 §2. */
export const VTS_THRESHOLDS = {
  /** ≥70: publica de inmediato, sin revisión. */
  green: 70,
  /** 45–69: publica de inmediato + revisión diferida invisible al autor. */
  amber: 45,
} as const;

export type VtsBand = 'green' | 'amber' | 'red';

export function vtsBand(score: number): VtsBand {
  if (score >= VTS_THRESHOLDS.green) return 'green';
  if (score >= VTS_THRESHOLDS.amber) return 'amber';
  return 'red';
}

/**
 * Estados del token. docs/09 §3.1, corregido por ADR-016.
 *
 * `PENDING` del documento NO está aquí: un claim en curso no es un token y
 * vive en su propia tabla. `DENIED` sí: se registra el intento para poder
 * analizarlo, aunque no exista token que enseñar.
 */
export const VisitTokenState = z.enum([
  'ISSUED',
  'CONSUMED',
  'EXPIRED',
  'REVOKED',
  'DENIED',
]);
export type VisitTokenState = z.infer<typeof VisitTokenState>;

/**
 * Muestra cruda de ubicación. Se envía tal cual al servidor.
 * `monotonicMs` viene de performance.now(), NO de Date.now():
 * el reloj de pared del dispositivo es manipulable por el usuario.
 */
export const LocationSample = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  accuracyM: z.number().nonnegative(),
  monotonicMs: z.number().nonnegative(),
});
export type LocationSample = z.infer<typeof LocationSample>;

/**
 * Ventana de acumulación de dwell. docs/09 §4.3, aplicada como TECHO por
 * ADR-016: un claim abierto y abandonado cuarenta minutos cuenta como seis.
 *
 * No le quita un punto a nadie honesto —S_dwell se satura a los 4 min— y le
 * quita al abuso el incentivo de dejar el claim abierto mientras se va.
 */
export const DWELL_WINDOW_MINUTES = 6;

/** Un claim sin cerrar más viejo que esto está abandonado. ADR-016 §1. */
export const CLAIM_ABANDON_MINUTES = 30;

/** Por debajo de esto no se emite token, sin importar el resto. docs/09 §2.2. */
export const HARD_MIN_DWELL_MINUTES = 2;

/** Intervalo de muestreo en el cliente. docs/09 §4.3. */
export const SAMPLE_INTERVAL_MS = 30_000;

/**
 * Proporción de muestras que deben caer dentro del radio, y mínimo absoluto.
 *
 * docs/09 §4.3 dice "7 de 12". ADR-016 lo hace proporcional porque iOS Safari
 * suspende la pestaña al bloquearse la pantalla y las 12 muestras no llegan:
 * la regla absoluta rechazaba por decisión del sistema operativo, no del
 * usuario. 0.58 es 7/12, así que la intención se conserva exacta.
 */
export const MIN_INSIDE_RATIO = 0.58;
export const MIN_SAMPLES = 4;

/** Códigos de error accionables. ADR-002 §5. */
export const VisitTokenError = z.enum([
  'need_visit_token',
  'token_expired',
  'token_already_used',
  'token_revoked',
  'visit_not_verified',
  'too_far_from_place',
  'dwell_too_short',
  'implausible_trajectory',
  'place_claim_rate_limited',
  'duplicate_recommendation',
]);
export type VisitTokenError = z.infer<typeof VisitTokenError>;

/**
 * Distancia entre dos puntos en metros (haversine).
 *
 * Solo para el pre-chequeo de UI en el cliente. El servidor usa
 * ST_DWithin sobre geography(Point,4326) — ver ADR-002 §3, donde se
 * explica por qué la columna es geography y no geometry.
 */
export function haversineMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const R = 6_371_000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Velocidad implícita entre dos puntos, en km/h.
 * Usada para detectar trayectorias imposibles (doc 09 §3).
 * Devuelve Infinity si Δt es 0 — dos claims simultáneos en lugares
 * distintos es precisamente el fraude que buscamos.
 */
export function implicitSpeedKmh(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
  deltaSeconds: number,
): number {
  if (deltaSeconds <= 0) return Number.POSITIVE_INFINITY;
  const meters = haversineMeters(a, b);
  return (meters / deltaSeconds) * 3.6;
}

/**
 * Umbrales de trayectoria. docs/09 §2.2, corregido por ADR-016.
 *
 * Eran un solo umbral de 200 km/h, que es velocidad de tren: quien va de CDMX
 * a Puebla y come en los dos extremos recibía rechazo. Con la tabla del
 * documento recibe −8, que es sobrevivible.
 */
export const TRAJECTORY_SUSPICIOUS_KMH = 120;
export const TRAJECTORY_IMPOSSIBLE_KMH = 400;

export type TrajectoryFlag = 'suspicious_travel' | 'impossible_travel';

/**
 * Penalización por velocidad implícita desde el claim anterior.
 *
 * `null` = primer claim de la cuenta, que no penaliza (docs/09 §2.2, última
 * fila de la tabla). S_traj NUNCA rechaza sola: resta del total.
 */
export function trajectoryPenalty(
  kmh: number | null,
): { resta: number; flag: TrajectoryFlag | null } {
  if (kmh === null) return { resta: 0, flag: null };
  if (kmh > TRAJECTORY_IMPOSSIBLE_KMH) {
    return { resta: 15, flag: 'impossible_travel' };
  }
  if (kmh >= TRAJECTORY_SUSPICIOUS_KMH) {
    return { resta: 8, flag: 'suspicious_travel' };
  }
  return { resta: 0, flag: null };
}

/**
 * Pre-chequeo LOCAL de presencia, exclusivamente para la UI.
 *
 * NO es una decisión de autorización: sirve para decirle al usuario
 * "estás aquí ✓, ya puedes escribir" antes de que redacte, en vez de
 * dejarlo escribir y rechazarlo después. El veredicto real lo emite
 * el servidor recalculando desde las muestras crudas.
 */
export function localPresencePrecheck(
  place: { lat: number; lng: number; profile?: GeofenceProfile },
  samples: readonly LocationSample[],
): { looksPresent: boolean; reason?: VisitTokenError } {
  const profile = GEOFENCE_PROFILES[place.profile ?? 'standard'];

  const usable = samples.filter((s) => s.accuracyM <= profile.maxAccuracyM);
  if (usable.length === 0) {
    return { looksPresent: false, reason: 'visit_not_verified' };
  }

  const inside = usable.filter(
    (s) => haversineMeters(place, s) <= profile.radiusM,
  );
  if (usable.length < MIN_SAMPLES) {
    return { looksPresent: false, reason: 'visit_not_verified' };
  }
  if (inside.length / usable.length < MIN_INSIDE_RATIO) {
    return { looksPresent: false, reason: 'too_far_from_place' };
  }

  return { looksPresent: true };
}
